import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { 
  Calendar, Clock, ChevronLeft, ChevronRight, Plus, Copy, Mail, 
  AlertTriangle, Check, X, Users, Edit2, Trash2, Send, Megaphone
} from 'lucide-react';
import { format, addDays, subDays, startOfWeek, parseISO, getDay } from 'date-fns';
import Modal from './Modal';
import AnnouncementComposer from './AnnouncementComposer';
import './ShiftScheduler.css';
import './AnnouncementComposer.css';

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ShiftScheduler({ onClose }) {
  const { 
    state, 
    addShift, 
    updateShift, 
    deleteShift, 
    copyWeekShifts,
    hasTimeOffOnDate,
    isShiftOutsideAvailability,
    getEmployeeById
  } = useApp();
  const { employees, shifts, timeOffRequests } = state;

  // Week navigation - start on Sunday
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 0 });
  });

  // Modal states
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    employeeId: '',
    date: '',
    startTime: '07:30',
    endTime: '16:00',
    routeName: '',
    notes: ''
  });

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMethod, setExportMethod] = useState('clipboard');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Announcement modal
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

  // Get active employees (including those still onboarding so new hires appear)
  const activeEmployees = useMemo(() => {
    return employees.filter(e => e.status === 'active' || e.status === 'onboarding');
  }, [employees]);

  // Generate week dates
  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Get shifts for current week
  const weekShifts = useMemo(() => {
    const startStr = format(weekStart, 'yyyy-MM-dd');
    const endStr = format(addDays(weekStart, 6), 'yyyy-MM-dd');
    return shifts.filter(s => s.date >= startStr && s.date <= endStr);
  }, [shifts, weekStart]);

  // Get shift for a specific employee and date
  const getShiftForCell = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return weekShifts.find(s => s.employeeId === employeeId && s.date === dateStr);
  };

  // Check if employee has time off on date
  const checkTimeOff = (employeeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return hasTimeOffOnDate(employeeId, dateStr);
  };

  // Check if employee is available on date
  const checkAvailability = (employeeId, date) => {
    const employee = getEmployeeById(employeeId);
    if (!employee?.availability) return { available: true };
    
    const dayName = DAYS_OF_WEEK[getDay(date)];
    const availability = employee.availability[dayName];
    
    return availability || { available: true };
  };

  // Navigate weeks
  const goToPreviousWeek = () => setWeekStart(prev => subDays(prev, 7));
  const goToNextWeek = () => setWeekStart(prev => addDays(prev, 7));
  const goToToday = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));

  // Open shift modal for new shift
  const openNewShiftModal = (employeeId, date) => {
    const employee = getEmployeeById(employeeId);
    const defaultStart = employee?.defaultShift?.startTime || '07:30';
    const defaultEnd = employee?.defaultShift?.endTime || '16:00';
    
    setEditingShift(null);
    setShiftForm({
      employeeId,
      date: format(date, 'yyyy-MM-dd'),
      startTime: defaultStart,
      endTime: defaultEnd,
      routeName: '',
      notes: ''
    });
    setShowShiftModal(true);
  };

  // Open shift modal for editing
  const openEditShiftModal = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      employeeId: shift.employeeId,
      date: shift.date,
      startTime: shift.startTime,
      endTime: shift.endTime,
      routeName: shift.routeName || '',
      notes: shift.notes || ''
    });
    setShowShiftModal(true);
  };

  // Save shift
  const handleSaveShift = async () => {
    if (!shiftForm.employeeId || !shiftForm.date) return;

    const shiftData = {
      employeeId: shiftForm.employeeId,
      date: shiftForm.date,
      startTime: shiftForm.startTime,
      endTime: shiftForm.endTime,
      routeName: shiftForm.routeName,
      notes: shiftForm.notes
    };

    if (editingShift) {
      await updateShift(editingShift.id, shiftData);
    } else {
      await addShift(shiftData);
    }

    setShowShiftModal(false);
    setEditingShift(null);
  };

  // Delete shift
  const handleDeleteShift = async () => {
    if (!editingShift) return;
    await deleteShift(editingShift.id);
    setShowShiftModal(false);
    setEditingShift(null);
  };

  // Copy previous week
  const handleCopyPreviousWeek = async () => {
    const prevWeekStart = format(subDays(weekStart, 7), 'yyyy-MM-dd');
    const currentWeekStart = format(weekStart, 'yyyy-MM-dd');
    
    if (weekShifts.length > 0) {
      if (!window.confirm('This will add shifts from last week. Existing shifts will not be replaced. Continue?')) {
        return;
      }
    }

    try {
      const created = await copyWeekShifts(prevWeekStart, currentWeekStart);
      if (!created || created.length === 0) {
        alert('No shifts found in the previous week to copy.');
      }
    } catch (err) {
      console.error('Failed to copy previous week:', err);
      alert("Couldn't copy last week's shifts. Please try again.");
    }
  };

  // Generate schedule text for export
  const generateScheduleText = () => {
    const weekLabel = `${format(weekStart, 'MMM d')} - ${format(addDays(weekStart, 6), 'MMM d, yyyy')}`;
    let text = `📅 SCHEDULE: Week of ${weekLabel}\n\n`;

    weekDates.forEach((date, dayIndex) => {
      if (dayIndex === 0 || dayIndex === 6) return; // Skip weekends if no shifts
      
      const dayShifts = weekShifts.filter(s => s.date === format(date, 'yyyy-MM-dd'));
      const dayOff = activeEmployees.filter(emp => 
        checkTimeOff(emp.id, date)
      );
      
      if (dayShifts.length === 0 && dayOff.length === 0) return;

      text += `${format(date, 'EEEE M/d').toUpperCase()}\n`;
      text += `━━━━━━━━━━━━━━━━\n`;

      // Group by route
      const byRoute = {};
      dayShifts.forEach(shift => {
        const route = shift.routeName || 'Unassigned';
        if (!byRoute[route]) byRoute[route] = [];
        const emp = getEmployeeById(shift.employeeId);
        byRoute[route].push({
          name: emp ? `${emp.firstName} ${emp.lastName || ''}`.trim() : 'Unknown',
          time: `${shift.startTime} - ${shift.endTime}`,
          role: emp?.role
        });
      });

      Object.entries(byRoute).forEach(([route, members]) => {
        text += `${route}\n`;
        members.forEach(m => {
          const roleLabel = m.role === 'manager' || m.role === 'lead' ? ' (Lead)' : '';
          text += `  • ${m.name} — ${m.time}${roleLabel}\n`;
        });
        text += '\n';
      });

      if (dayOff.length > 0) {
        text += `OFF: ${dayOff.map(e => `${e.firstName} ${e.lastName || ''}`.trim()).join(', ')}\n\n`;
      }
    });

    return text;
  };

  // Copy to clipboard
  const handleCopyToClipboard = async () => {
    const text = generateScheduleText();
    await navigator.clipboard.writeText(text);
    alert('Schedule copied to clipboard!');
  };

  // Send email to staff
  const handleSendEmail = async () => {
    setSendingEmail(true);
    
    const employeesWithEmail = activeEmployees.filter(e => e.email);
    
    if (employeesWithEmail.length === 0) {
      alert('No employees have email addresses on file.');
      setSendingEmail(false);
      return;
    }

    const weekLabel = `${format(weekStart, 'MMM d')} - ${format(addDays(weekStart, 6), 'MMM d, yyyy')}`;
    const scheduleText = generateScheduleText();

    try {
      // Call the email API
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'schedule',
          data: {
            recipients: employeesWithEmail.map(e => ({
              email: e.email,
              name: `${e.firstName} ${e.lastName || ''}`.trim()
            })),
            subject: `📅 Two Sisters Schedule: Week of ${weekLabel}`,
            scheduleText,
            weekLabel
          }
        })
      });

      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => {
          setEmailSent(false);
          setShowExportModal(false);
        }, 2000);
      } else {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.details || result.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending schedule email:', error);
      alert(`Failed to send email. ${error.message || 'Please try again.'}`);
    } finally {
      setSendingEmail(false);
    }
  };

  // Calculate week stats
  const weekStats = useMemo(() => {
    const shiftCount = weekShifts.length;
    const employeesScheduled = new Set(weekShifts.map(s => s.employeeId)).size;
    const timeOffCount = activeEmployees.filter(emp => 
      weekDates.some(date => checkTimeOff(emp.id, date))
    ).length;
    
    // Count availability warnings
    let warnings = 0;
    weekShifts.forEach(shift => {
      const employee = getEmployeeById(shift.employeeId);
      if (employee?.availability) {
        const date = parseISO(shift.date);
        const dayName = DAYS_OF_WEEK[getDay(date)];
        const avail = employee.availability[dayName];
        if (!avail?.available || shift.startTime < avail.start || shift.endTime > avail.end) {
          warnings++;
        }
      }
    });

    return { shiftCount, employeesScheduled, timeOffCount, warnings };
  }, [weekShifts, activeEmployees, weekDates]);

  return (
    <div className="shift-scheduler">
      {/* Header */}
      <div className="scheduler-header">
        <div className="header-left">
          <h2><Calendar size={24} /> Shift Schedule</h2>
          <p className="subtitle">Manage weekly employee shifts</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-gold" onClick={() => setShowAnnouncementModal(true)}>
            <Megaphone size={18} />
            Announcement
          </button>
          <button className="btn btn-secondary" onClick={handleCopyPreviousWeek}>
            <Copy size={18} />
            Copy Previous Week
          </button>
          <button className="btn btn-secondary" onClick={() => setShowExportModal(true)}>
            <Send size={18} />
            Share Schedule
          </button>
          {onClose && (
            <button className="btn btn-secondary" onClick={onClose}>
              <X size={18} />
              Close
            </button>
          )}
        </div>
      </div>

      {/* Week Navigation */}
      <div className="week-navigation">
        <button className="nav-btn" onClick={goToPreviousWeek}>
          <ChevronLeft size={20} />
        </button>
        <div className="week-label">
          Week of {format(weekStart, 'MMMM d')} - {format(addDays(weekStart, 6), 'MMMM d, yyyy')}
        </div>
        <button className="nav-btn" onClick={goToNextWeek}>
          <ChevronRight size={20} />
        </button>
        <button className="btn btn-sm btn-secondary" onClick={goToToday}>
          Today
        </button>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat">
          <span className="stat-value">{weekStats.shiftCount}</span>
          <span className="stat-label">Shifts</span>
        </div>
        <div className="stat">
          <span className="stat-value">{weekStats.employeesScheduled}</span>
          <span className="stat-label">Employees</span>
        </div>
        <div className="stat">
          <span className="stat-value">{weekStats.timeOffCount}</span>
          <span className="stat-label">Time Off</span>
        </div>
        {weekStats.warnings > 0 && (
          <div className="stat warning">
            <AlertTriangle size={16} />
            <span className="stat-value">{weekStats.warnings}</span>
            <span className="stat-label">Warnings</span>
          </div>
        )}
      </div>

      {/* Schedule Grid */}
      <div className="schedule-grid">
        <table className="schedule-table">
          <thead>
            <tr>
              <th className="employee-header">Employee</th>
              {weekDates.map((date, idx) => (
                <th key={idx} className={`day-header ${idx === 0 || idx === 6 ? 'weekend' : ''}`}>
                  <span className="day-name">{format(date, 'EEE')}</span>
                  <span className="day-date">{format(date, 'M/d')}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeEmployees.map(employee => (
              <tr key={employee.id}>
                <td className="employee-cell">
                  <div className="employee-info">
                    <div className="employee-avatar">{employee.avatar || employee.firstName[0]}</div>
                    <div className="employee-details">
                      <span className="employee-name">{employee.firstName} {employee.lastName}</span>
                      <span className="employee-role">{employee.role}</span>
                    </div>
                  </div>
                </td>
                {weekDates.map((date, idx) => {
                  const shift = getShiftForCell(employee.id, date);
                  const hasTimeOff = checkTimeOff(employee.id, date);
                  const availability = checkAvailability(employee.id, date);
                  const isUnavailable = !availability.available;
                  
                  // Check if shift is outside availability
                  let outsideAvailability = false;
                  if (shift && availability.available) {
                    if (shift.startTime < availability.start || shift.endTime > availability.end) {
                      outsideAvailability = true;
                    }
                  }

                  return (
                    <td 
                      key={idx} 
                      className={`shift-cell ${idx === 0 || idx === 6 ? 'weekend' : ''}`}
                    >
                      {hasTimeOff ? (
                        <div className="shift-off">
                          🏖️ Time Off
                        </div>
                      ) : shift ? (
                        <div 
                          className={`shift-block ${outsideAvailability ? 'warning' : ''}`}
                          onClick={() => openEditShiftModal(shift)}
                        >
                          <div className="shift-time">
                            {shift.startTime} - {shift.endTime}
                          </div>
                          {shift.routeName && (
                            <div className="shift-route">{shift.routeName}</div>
                          )}
                          {outsideAvailability && (
                            <div className="availability-warning">
                              <AlertTriangle size={12} /> Outside availability
                            </div>
                          )}
                        </div>
                      ) : isUnavailable ? (
                        <div className="shift-unavailable">
                          Not Available
                        </div>
                      ) : (
                        <div 
                          className="shift-empty"
                          onClick={() => openNewShiftModal(employee.id, date)}
                        >
                          + Add
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Shift Edit Modal */}
      <Modal
        isOpen={showShiftModal}
        onClose={() => setShowShiftModal(false)}
        title={editingShift ? 'Edit Shift' : 'Add Shift'}
      >
        <div className="shift-form">
          <div className="form-group">
            <label>Employee</label>
            <select
              value={shiftForm.employeeId}
              onChange={(e) => setShiftForm({ ...shiftForm, employeeId: e.target.value })}
              disabled={!!editingShift}
            >
              <option value="">Select employee...</option>
              {activeEmployees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={shiftForm.date}
              onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })}
              disabled={!!editingShift}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Time</label>
              <input
                type="time"
                value={shiftForm.startTime}
                onChange={(e) => setShiftForm({ ...shiftForm, startTime: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End Time</label>
              <input
                type="time"
                value={shiftForm.endTime}
                onChange={(e) => setShiftForm({ ...shiftForm, endTime: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Route/Team Name</label>
            <input
              type="text"
              value={shiftForm.routeName}
              onChange={(e) => setShiftForm({ ...shiftForm, routeName: e.target.value })}
              placeholder="e.g., Route A, Team 1"
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={shiftForm.notes}
              onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })}
              placeholder="Any special notes for this shift..."
              rows={2}
            />
          </div>

          <div className="form-actions">
            {editingShift && (
              <button className="btn btn-danger" onClick={handleDeleteShift}>
                <Trash2 size={16} /> Delete
              </button>
            )}
            <div className="spacer" />
            <button className="btn btn-secondary" onClick={() => setShowShiftModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveShift}>
              <Check size={16} /> {editingShift ? 'Update' : 'Add'} Shift
            </button>
          </div>
        </div>
      </Modal>

      {/* Export Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Share Schedule"
      >
        {emailSent ? (
          <div className="success-state">
            <div className="success-icon">✓</div>
            <h3>Schedule Sent!</h3>
            <p>Email sent to {activeEmployees.filter(e => e.email).length} employees</p>
          </div>
        ) : (
          <div className="export-content">
            <div className="export-options">
              <div 
                className={`export-option ${exportMethod === 'clipboard' ? 'selected' : ''}`}
                onClick={() => setExportMethod('clipboard')}
              >
                <div className="option-icon">📋</div>
                <div className="option-details">
                  <h4>Copy to Clipboard</h4>
                  <p>Copy formatted text to paste in group chat</p>
                </div>
              </div>
              <div 
                className={`export-option ${exportMethod === 'email' ? 'selected' : ''}`}
                onClick={() => setExportMethod('email')}
              >
                <div className="option-icon">📧</div>
                <div className="option-details">
                  <h4>Email to All Staff</h4>
                  <p>Send to {activeEmployees.filter(e => e.email).length} employees with email</p>
                </div>
              </div>
            </div>

            <div className="preview-section">
              <h4>Preview</h4>
              <pre className="schedule-preview">{generateScheduleText()}</pre>
            </div>

            <div className="export-actions">
              <button className="btn btn-secondary" onClick={() => setShowExportModal(false)}>
                Cancel
              </button>
              {exportMethod === 'clipboard' ? (
                <button className="btn btn-primary" onClick={handleCopyToClipboard}>
                  <Copy size={16} /> Copy to Clipboard
                </button>
              ) : (
                <button 
                  className="btn btn-primary" 
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? (
                    <>Sending...</>
                  ) : (
                    <><Mail size={16} /> Send to All Staff</>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Announcement Composer Modal */}
      <AnnouncementComposer
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        onSuccess={() => {}}
      />
    </div>
  );
}

export default ShiftScheduler;
